import { useCallback, useEffect, useRef } from 'react'
import { useAtomValue } from 'jotai'
import { useReducedMotion } from '@mantine/hooks'
import {
  activeSpeakerIdAtom,
  activeSpeakingAtom,
  currentVisemeAtom,
} from '../../../atoms/speechAtoms'
import { VISEME_SHAPE } from '../visemeShapes'

// Smoothing constants for the speech "envelope" — how the amplitude rises/falls
// each frame so it reads like a natural mouth rather than stepping between
// phonemes.
const ATTACK = 0.5 // rise toward a louder viseme
const DECAY = 0.14 // fall back toward silence

// The raw VISEME_SHAPE.openY values are tuned for MOUTH OPENNESS, where even an
// open vowel only reaches ~0.85 and most consonants sit at 0.2–0.3. That reads
// as a near-flat visualizer. Map openness -> visualizer amplitude with a gain +
// floor so speech drives tall, lively bars: a curve that lifts the mid-range and
// adds a baseline so any voiced phoneme registers.
const GAIN = 1.9 // overall amplitude multiplier
const FLOOR = 0.18 // any non-silent phoneme reads at least this tall
function toAmplitude(openY) {
  if (openY <= 0.05) return 0 // silence/closed -> truly flat
  // sqrt lifts the low/mid range (where most phonemes live) so they're visible.
  const lifted = Math.sqrt(openY)
  return Math.min(1, FLOOR + lifted * GAIN * (1 - FLOOR))
}

/**
 * Drives a smoothed speech amplitude (0..1) from the live viseme stream, for any
 * visualizer style. Returns a ref-based reader so the visualizer's own rAF loop
 * can sample it every frame WITHOUT re-rendering React.
 *
 * The amplitude is derived from the SAME signal that powers lip-sync
 * (VISEME_SHAPE.openY), scaled up for visualizer punch and smoothed into an
 * attack/decay envelope. This is the only speech signal that survives our CORS
 * setup (real-audio FFT would read a tainted/silent stream), so every visualizer
 * style is built on it.
 *
 * @param {object}  character          the character this visualizer represents
 * @param {object}  [opts]
 * @param {boolean} [opts.anyVoice]    animate whenever ANY voice is speaking
 *   (narrator included), not only when this exact character is the speaker. Hero
 *   visualizers want this; the per-character cast-dock mouth does not.
 * @returns {{ read: () => number, activeRef: import('react').MutableRefObject<boolean>, reduceMotion: boolean }}
 */
export function useVisemeEnvelope(character, { anyVoice = false } = {}) {
  const activeSpeakerId = useAtomValue(activeSpeakerIdAtom)
  const anySpeaking = useAtomValue(activeSpeakingAtom)
  const viseme = useAtomValue(currentVisemeAtom)
  const reduceMotion = useReducedMotion()

  // anyVoice (hero visualizer): animate whenever ANY voice is playing — the
  // narrator included. We OR every "a voice is live" signal rather than trust a
  // single atom, so the bars animate no matter which of them the play path set:
  // the explicit speaking flag, a non-null speaker id, OR an active viseme. (A
  // live viseme alone is sufficient — it only advances while audio plays.)
  const isSpeaking = anyVoice
    ? (anySpeaking || activeSpeakerId != null || viseme !== 'sil')
    : (!!character && activeSpeakerId === character.id)

  const visemeRef = useRef(viseme)
  const activeRef = useRef(isSpeaking)
  const envRef = useRef(0)

  useEffect(() => {
    visemeRef.current = viseme
  }, [viseme])
  useEffect(() => {
    activeRef.current = isSpeaking
  }, [isSpeaking])

  // Advance + return the smoothed envelope. Visualizers call this once per frame
  // from their draw loop (never during render), so reading refs here is safe.
  const read = useCallback(() => {
    const target = activeRef.current
      ? toAmplitude(VISEME_SHAPE[visemeRef.current]?.openY ?? 0)
      : 0
    const k = target > envRef.current ? ATTACK : DECAY
    envRef.current += (target - envRef.current) * k
    return envRef.current
  }, [])

  // visemeRef exposes the CURRENT phoneme to a style's draw loop (e.g. BarsViz
  // shapes its band profile per phoneme — sibilants push high bars, vowels push
  // low/mid — so the bars resemble real speech spectra, not a uniform pulse).
  return { read, activeRef, visemeRef, reduceMotion }
}
